<div class="h-full min-h-0">
	<header class="page-header">
		<h2>Document Formatting</h2>
	</header>

	<div class="grid h-full min-h-0 overflow-y-scroll gap-4">
		<ui-section
			*ngIf="
				(visibleTemplates$ | async) ===
					BusinessSettingsVisibleTemplates.HTML_WORD ||
				(visibleTemplates$ | async) === BusinessSettingsVisibleTemplates.WORD
			"
			title="Letterhead & Footer"
			[collapsable]="true"
		>
			<ui-button
				title-action
				label="Add New"
				icon="plus"
				(click)="addBaseDocx()"
			></ui-button>
			<app-business-base-docx-table
				section-content
			></app-business-base-docx-table>
		</ui-section>

		<ui-section title="Editor Default Settings" [collapsable]="true">
			<div section-content class="flex flex-col gap-3">
				<div class="flex gap-4">
					<div class="w-96">
						<app-dropdown
							[testHook]="testHooks.FontDropdown"
							label="Default document font"
							[itemGroups]="[{ items: fontDropdownOptions }]"
							(onSelect)="setSelectedFont($event)"
							[value]="defaultFont"
						></app-dropdown>
					</div>
					<div>
						<app-dropdown
							label="Font size"
							[itemGroups]="[{ items: fontSizeDropdownOptions }]"
							(onSelect)="setSelectedFontSize($event)"
							[value]="defaultFontSize$ | async"
						></app-dropdown>
					</div>
					<div>
						<app-dropdown
							label="Line-height"
							[itemGroups]="[{ items: fontLineHeightDropdownOptions }]"
							(onSelect)="setSelectedFontLineHeight($event)"
							[value]="defaultLineHeight$ | async"
						></app-dropdown>
					</div>
				</div>
				<ui-section class="self-start" [title]="pageLayoutHeadingText">
					<ui-button
						title-action
						label="Edit"
						icon="edit"
						(click)="editPageSize()"
					></ui-button>
					<div section-content class="grid grid-cols-1">
						<div class="grid gap-4 grid-cols-[auto_1fr]">
							<ng-container *ngIf="useHtmlDocuments$ | async">
								<label>Page size</label>
								<div class="flex">
									<app-pill [mode]="PillModes.secondary">{{
										defaultPageSize$ | async | capitalize
									}}</app-pill>
								</div>
							</ng-container>

							<label>Margins</label>
							<div *ngIf="defaultPageMargins$ | async as margins" class="flex">
								<app-pill [mode]="PillModes.secondary"
									><span class="opacity-50">&uarr;</span
									>{{ margins.top }}</app-pill
								>
								<app-pill [mode]="PillModes.secondary"
									><span class="opacity-50">&darr;</span
									>{{ margins.bottom }}</app-pill
								>
								<app-pill [mode]="PillModes.secondary"
									><span class="opacity-50">&larr;</span
									>{{ margins.left }}</app-pill
								>
								<app-pill [mode]="PillModes.secondary"
									><span class="opacity-50">&rarr;</span
									>{{ margins.right }}</app-pill
								>
								<app-pill [mode]="PillModes.secondary">{{
									margins.unit | unitOfMeasurement
								}}</app-pill>
							</div>
						</div>

						<div class="flex w-fit mt-3" *ngIf="useHtmlDocuments$ | async">
							<app-checkbox
								label="Use default margins for word export"
								[ngModel]="settings?.documents?.useDefaultMarginsForWordExport"
								(ngModelChange)="setDefaultMarginsForWordExport($event)"
							></app-checkbox>
						</div>
					</div>
				</ui-section>
			</div>
		</ui-section>

		<ui-section title="Formatting Options" [collapsable]="true">
			<div section-content class="flex flex-col gap-3">
				<div class="flex gap-4">
					<div>
						<app-dropdown
							label="Signature height"
							[itemGroups]="[{ items: signatureHeightDropdownOptions }]"
							(onSelect)="setSelectedSignatureHeight($event)"
							[value]="defaultSignatureHeight$ | async | capitalize"
						></app-dropdown>
					</div>
				</div>
				<div>
					<app-checkbox
						type="clear"
						[ngModel]="settings?.trustLedger?.hide0Values"
						(ngModelChange)="setHide0Values($event)"
						>Hide $0.00 amounts in Trust ledger document</app-checkbox
					>
				</div>
				<div>
					<app-checkbox
						type="clear"
						[ngModel]="settings?.statementOfAccount?.hide0Values"
						(ngModelChange)="setHide0ValuesInStatementOfAccount($event)"
						>Hide $0.00 amounts in statement of account table</app-checkbox
					>
				</div>
				<div *ngIf="useHtmlDocuments$ | async">
					<app-checkbox
						type="clear"
						[ngModel]="settings?.hideZebraStripes"
						(ngModelChange)="setZebraStripes($event)"
						>Hide zebra stripes in document tables</app-checkbox
					>
				</div>
				<div>
					<app-checkbox
						type="clear"
						[ngModel]="settings?.hideAuthorityLine"
						(ngModelChange)="setAuthorityLine($event)"
						>Hide binding authority statement in signature block</app-checkbox
					>
				</div>
				<div>
					<app-checkbox
						type="clear"
						[ngModel]="settings?.sublistSingleLine"
						(ngModelChange)="setSublistSingleLineBreaks($event)"
						>Apply single line spacing for sub-list items in
						documents</app-checkbox
					>
				</div>
				<div>
					<app-checkbox
						type="clear"
						[ngModel]="settings?.documents?.singleWitnessSignature"
						(ngModelChange)="setDocumentWitness($event)"
						>Show a single witness signature</app-checkbox
					>
				</div>
				<ui-section title="Dates">
					<div section-content class="flex flex-col gap-3">
						<div class="flex gap-10">
							<div class="flex flex-col gap-3">
								<div class="font-bold mb-3">
									Tag example:
									<ng-container ngNonBindable>{{ closingDate }}</ng-container>
								</div>
								<app-dropdown
									label="Short date format"
									[value]="selectedShortDateFormat"
									[itemGroups]="shortDateGroup"
									(onSelect)="onShortDateFormatSelect($event)"
								></app-dropdown>
							</div>
							<div class="flex flex-col gap-3">
								<div class="font-bold mb-3">
									Tag example:
									<ng-container ngNonBindable>{{
										closingLongDate
									}}</ng-container>
								</div>
								<app-dropdown
									label="Long date format"
									[value]="selectedLongDateFormat"
									[itemGroups]="longDateGroup"
									(onSelect)="onLongDateFormatSelect($event)"
								></app-dropdown>
							</div>
						</div>
					</div>
				</ui-section>
			</div>
		</ui-section>

		<app-name-formatting-settings></app-name-formatting-settings>

		<app-address-formatting></app-address-formatting>
	</div>
</div>
